import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { isRefusal, refusalText } from "../../../api/errors";
import { runCommand } from "../../../api/endpoints";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { ElementChanges, ElementView, Id } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { useStillEditing } from "../../../live";
import { Breadcrumb, Button, Card, EmptyState, Loading, showToast } from "../../../ui";
import { ElementActions } from "../components/ElementActions";
import { LockBanner } from "../components/LockBanner";
import { Check, FormError, RadioGroup, TextArea } from "../components/fields";
import { ElementMarks, LineItem } from "../components/marks";
import { NameFields, NeededByFields, commonFromElement, neededByOf, textToSend, validateCommon, type CommonValues } from "../forms/Common";
import { KIND_SPECS } from "../forms";
import type { Errors, KindSpec, Values } from "../forms/types";
import { useCommandId, useRefresh } from "../hooks";
import { isGone, isSettled, othersPending } from "../lib/badges";
import { sameContent } from "../lib/json";
import { chapterPath, elementPath } from "../lib/paths";
import { isAddableKind, kindWord } from "../lib/words";

type LockState = "taking" | "held" | "refused";

/** The screen at /elements/:id/edit: change an element in place, or as a Next version when it is validated or loaded. */
export function EditElementPage() {
  const { id = "" } = useParams();
  const query = useApiQuery("element", { id }, { enabled: !!id });

  if (query.isPending) return <Loading>Reading the element</Loading>;
  if (query.isError) {
    return (
      <Card>
        <EmptyState title="This element could not be opened">{refusalText(query.error)}</EmptyState>
      </Card>
    );
  }
  return <EditContent view={query.data} reload={() => void query.refetch()} />;
}

function EditContent({ view, reload }: { view: ElementView; reload: () => void }) {
  const row = view.element;
  const kind = row.kind;
  // The server does not take an edit of a rejected element; it asks for new reasons, or a withdrawal (workflow 16).
  if (row.state === "rejected") {
    return (
      <Card aria-labelledby="edit-title">
        <h1 id="edit-title">{row.name}</h1>
        <EmptyState title="The SME rejected it">
          It cannot be changed as it is. Propose it again with new reasons, or withdraw it. <Link to={elementPath(row)}>Open it</Link> to read the SME&apos;s reason.
        </EmptyState>
        <ElementActions element={row} editable />
      </Card>
    );
  }
  if (isGone(row)) {
    return (
      <Card aria-labelledby="edit-title">
        <h1 id="edit-title">{row.name}</h1>
        <EmptyState title="It has been withdrawn">A withdrawn element cannot be changed. <Link to={elementPath(row)}>Open it</Link> to see its history.</EmptyState>
      </Card>
    );
  }
  if (!isAddableKind(kind)) {
    return (
      <Card aria-labelledby="edit-title">
        <h1 id="edit-title">{row.name}</h1>
        <EmptyState title="This kind of element is not changed on a form">
          {kind === "spec_chapter" ? (
            <Link to={chapterPath(row.code)}>Open it in the chapter editor</Link>
          ) : (
            <>It comes with the starting pack. <Link to={elementPath(row)}>Open it</Link> to read it.</>
          )}
        </EmptyState>
      </Card>
    );
  }
  return <EditForm key={`${row.id}:${row.version}`} view={view} spec={KIND_SPECS[kind]} reload={reload} />;
}

function EditForm({ view, spec, reload }: { view: ElementView; spec: KindSpec; reload: () => void }) {
  const row = view.element;
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { ping } = useStillEditing(row.id);
  const [attempt, setAttempt] = useState(0);
  const [lockState, setLockState] = useState<LockState>("taking");
  const [lockError, setLockError] = useState<string | null>(null);
  const released = useRef(false);

  const [common, setCommon] = useState<CommonValues>(() => commonFromElement(row));
  const [values, setValues] = useState<Values>(() => spec.fromElement(row, view.lines));
  const [errors, setErrors] = useState<Errors>({});
  const [removed, setRemoved] = useState<Id[]>([]);
  const [meaning, setMeaning] = useState<"" | "yes" | "no">("");
  const [reason, setReason] = useState("");
  const [light, setLight] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const edit = useApiCommand("edit_element");
  const ids = useCommandId();
  const Fields = spec.Fields;
  const settled = isSettled(row);
  const me = useCurrentMe();
  // Someone else's Next version waits on it (UI-11, R2-07): this change is added to it, and the SME sees one Next version.
  const addingTo = othersPending(row, me.active?.membership_id);

  // Take the lock when the form opens and give it back when it closes without saving.
  useEffect(() => {
    let alive = true;
    let held = false;
    released.current = false;
    runCommand("take_lock", { id: row.id })
      .then(() => {
        held = true;
        if (alive) setLockState("held");
        else void runCommand("release_lock", { id: row.id, reason: "cancelled" }).catch(() => undefined);
      })
      .catch((error: unknown) => {
        if (!alive) return;
        setLockState("refused");
        setLockError(refusalText(error));
      });
    return () => {
      alive = false;
      if (held && !released.current) void runCommand("release_lock", { id: row.id, reason: "cancelled" }).catch(() => undefined);
    };
  }, [row.id, attempt]);

  const retryLock = () => {
    setLockState("taking");
    setLockError(null);
    setAttempt((n) => n + 1);
  };

  const changeCommon = (patch: Partial<CommonValues>) => {
    ping();
    setCommon((c) => ({ ...c, ...patch }));
  };
  const changeValues = (patch: Partial<Values>) => {
    ping();
    setValues((v) => ({ ...v, ...patch }));
  };

  const plan = useMemo(() => {
    const text = textToSend(common, spec, values);
    const content = spec.toContent(values, row.content);
    const diff = spec.diffLines?.(values, view.lines) ?? { add: [], change: [], remove: [] };
    const needed = neededByOf(common);
    const changes: ElementChanges = {};
    if (text.name !== row.name) changes.name = text.name;
    if (text.definition !== row.definition) changes.definition = text.definition;
    if (!sameContent(content, row.content)) changes.content = content;
    if (needed && !sameContent(needed, row.needed_by ?? null)) changes.needed_by = needed;
    const lineRemove = [...diff.remove, ...removed];
    const lineChanges = diff.add.length + diff.change.length + lineRemove.length;
    const wordingOnly = !changes.content && lineChanges === 0 && !changes.needed_by;
    return { changes, diff, lineRemove, lineChanges, wordingOnly, nothing: Object.keys(changes).length === 0 && lineChanges === 0 };
  }, [common, values, removed, spec, row, view.lines]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = { ...validateCommon(common, spec, values), ...spec.validate(values, "edit") };
    setErrors(found);
    setProblem(null);
    if (Object.keys(found).length > 0) return;
    if (plan.nothing) {
      setProblem("You have not changed anything yet.");
      return;
    }
    if (settled && meaning === "") {
      setProblem("Say whether this change changes what the element means.");
      return;
    }
    if (settled && meaning === "yes" && !reason.trim()) {
      setProblem("Say in a sentence what changes in its meaning.");
      return;
    }
    const lightOk = settled && meaning === "no" && plan.wordingOnly && light;
    edit.mutate(
      {
        id: row.id,
        expected_version: row.version,
        changes: plan.changes,
        lines_add: plan.diff.add,
        lines_change: plan.diff.change,
        lines_remove: plan.lineRemove,
        changes_meaning: settled ? { value: meaning === "yes", reason: reason.trim() } : null,
        light: lightOk ? true : null,
        ...(addingTo ? { add_to_pending: true } : {}),
        command_id: ids.current(),
      },
      {
        onSuccess: () => {
          ids.renew();
          released.current = true;
          void runCommand("release_lock", { id: row.id, reason: "saved" }).catch(() => undefined);
          refresh("element", "item_page", "item_snapshot", "list_elements", "family_tree", "inbox", "sme_card", "home", "find_in_ontology");
          showToast({
            text: addingTo
              ? `Saved. Your change is added to ${addingTo}'s Next version, which goes to the SME as one.`
              : settled
                ? "Saved. Your change goes to the SME as a Next version."
                : "Saved.",
          });
          void navigate(elementPath(row));
        },
      },
    );
  };

  const cancel = () => {
    void navigate(elementPath(row));
  };

  const disabled = lockState !== "held";
  const libraryLines = view.lines.filter((line) => (line.kind === "mapping" || line.kind === "standards") && line.origin !== "app");
  const otherLines = view.lines.filter((line) => line.kind !== "mapping" && line.kind !== "standards" && !(spec.diffLines && line.kind === "pick_value"));
  const stale = isRefusal(edit.error, "stale");
  const lockedOut = isRefusal(edit.error, "locked");

  return (
    <form onSubmit={submit} noValidate className="stack item-screen" aria-labelledby="edit-title">
      <Card>
        <Breadcrumb crumbs={view.breadcrumb} />
        <h1 id="edit-title">Change {row.name}</h1>
        <p className="muted">
          {kindWord(row.kind)} <code>{row.code}</code> <ElementMarks element={row} />
        </p>
        {addingTo ? (
          <p role="note">
            You add to {addingTo}&apos;s pending change. The SME sees one Next version with both changes. A part you both change in different ways is refused, and the
            message names it.
          </p>
        ) : settled ? (
          <p>This element is {row.origin === "loaded" ? "loaded content" : "validated"}. A change becomes a Next version for the SME. What the SME validated stays official until then.</p>
        ) : (
          <p>This element is not validated yet, so a change is made in place.</p>
        )}
        {lockState === "taking" && <Loading>Taking the edit for you</Loading>}
        {lockState === "refused" && (
          <>
            <p role="alert" className="form-error">
              {lockError}
            </p>
            <LockBanner elementId={row.id} lock={view.lock} onTakenOver={retryLock} />
            <div className="row">
              <Button onClick={retryLock}>Try again</Button>
              <Button variant="quiet" onClick={reload}>
                Read the latest
              </Button>
            </div>
          </>
        )}
      </Card>

      <fieldset className="edit-fields" disabled={disabled}>
        <Card>
          <NameFields spec={spec} values={common} errors={errors} showCode={false} onChange={changeCommon} />
        </Card>
        <Card aria-label="Details">
          <Fields values={values} errors={errors} mode="edit" elementId={row.id} set={changeValues} />
        </Card>
        {libraryLines.length > 0 && (
          <Card aria-label="Library mapping lines">
            <h2>Library mapping lines</h2>
            <p className="muted">Tick a line to take it out. To add a line, use the library browser.</p>
            <ul className="line-list">
              {libraryLines.map((line) => (
                <li key={line.id} className="line-edit">
                  <Check
                    label="Take this line out"
                    checked={removed.includes(line.id)}
                    onChange={(on) => {
                      ping();
                      setRemoved((current) => (on ? [...current, line.id] : current.filter((x) => x !== line.id)));
                    }}
                  />
                  <ul className="line-list">
                    <LineItem line={line} />
                  </ul>
                </li>
              ))}
            </ul>
          </Card>
        )}
        {otherLines.length > 0 && (
          <Card aria-label="Other lines">
            <h2>Other lines</h2>
            <p className="muted">These are changed where they are made: the item page for required data, the symbol picker for symbols.</p>
            <ul className="line-list">
              {otherLines.map((line) => (
                <LineItem key={line.id} line={line} />
              ))}
            </ul>
          </Card>
        )}
        <Card aria-label="Needed by">
          <NeededByFields values={common} onChange={changeCommon} />
        </Card>
        {settled && (
          <Card aria-label="Does this change what it means?">
            <RadioGroup
              legend="Does this change what the element means?"
              required
              hint="Adding a value to a list does not change meaning. Taking a value out, or changing what it stands for, does."
              value={meaning}
              options={[
                { value: "no", label: "No, the meaning stays" },
                { value: "yes", label: "Yes, the meaning changes" },
              ]}
              onChange={(next) => {
                ping();
                setMeaning(next as "yes" | "no");
              }}
            />
            {meaning !== "" && (
              <TextArea
                label={meaning === "yes" ? "What changes in its meaning?" : "Anything the SME should know (optional)"}
                required={meaning === "yes"}
                rows={2}
                value={reason}
                onChange={(text) => {
                  ping();
                  setReason(text);
                }}
              />
            )}
            {meaning === "no" && plan.wordingOnly && !plan.nothing && (
              <Check
                label="This is a small edit of wording only"
                hint="The SME sees it as 'edited since you validated', with before and after, and settles it in one click."
                checked={light}
                onChange={setLight}
              />
            )}
          </Card>
        )}
      </fieldset>

      <Card>
        <div className="row">
          <Button type="submit" variant="primary" busy={edit.isPending} disabled={disabled}>
            {settled ? "Send as a Next version" : "Save the change"}
          </Button>
          <Button onClick={cancel}>Cancel</Button>
        </div>
        <FormError message={problem} />
        <FormError message={edit.error ? refusalText(edit.error) : null} />
        {stale && (
          <div className="row">
            <Button
              variant="secondary"
              onClick={() => {
                edit.reset();
                reload();
              }}
            >
              Read the latest version
            </Button>
            <span className="muted">Reading it again starts the form over with what is there now.</span>
          </div>
        )}
        {lockedOut && <LockBanner elementId={row.id} lock={view.lock} />}
      </Card>
    </form>
  );
}
